/**
 * Feature hook: Checkout / Undo Checkout document.
 * API → slice → hook → component with loading and result dialog (same pattern as useUploadToOneDrive).
 * Uses documentCheckoutFlowSlice for normal flow; legacy onCheckoutDocuments for retry/rollback.
 */
import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../hooks";
import { onCheckoutDocuments } from "../slices/documentsAction";
import {
  performCheckoutThunk,
  closeCheckoutDialog,
  clearCheckoutFileToOpen,
} from "../slices/documents/documentCheckoutFlowSlice";
import { useTools } from "./useTools";
import {
  DATA_LOAD_STATUS,
  CHECKOUT_STATUS,
  CHECKOUT_TYPE,
  REFRESH_TOKEN_ACTION,
  RESPONSE_TYPE,
  FILE_STATE,
} from "../constants/metaData";
import { getDocClassCodeOnly } from "../../functions/helperMethod";

const DEFAULT_TOKEN_REFRESH_PRE_ACTION = {
  retry: 0,
  actionType: REFRESH_TOKEN_ACTION.documentCheckoutLoad,
  refreshResponse: RESPONSE_TYPE.none,
};

function buildCheckoutList(item) {
  if (!item) return [];
  const encodedTitle = encodeURIComponent(item.title ?? "");
  return [
    {
      class: getDocClassCodeOnly(item.class ?? item.docClass ?? ""),
      docNo: item.docNo,
      sheetNo: item.sheetNo,
      docRev: item.docRev,
      docType: item.docType,
      fileNo: item.fileNo,
      title: encodedTitle,
      fileExtension: item.fileExtension,
      state: item.state,
      fileState: item.fileState,
      owner: item.owner,
      lastActionDate: new Date().toISOString().split("T")[0],
      isDownloaded: false,
    },
  ];
}

function buildCheckoutListFromItems(items) {
  return (items ?? []).flatMap((item) => buildCheckoutList(item));
}

function isEligibleForBulkCheckout(item) {
  if (!item) return false;
  if (item.isCheckoutDisable) return false;
  if (item.isReleased) return false;
  if (item.isCheckedOut) return false;
  return item.fileState === FILE_STATE.checkedIn;
}

function isEligibleForBulkUndoCheckout(item) {
  if (!item) return false;
  if (item.isReleased) return false;
  if (item.isCheckedOut) return true;
  return item.fileState === FILE_STATE.checkedOut;
}

export function useCheckout() {
  const dispatch = useAppDispatch();
  const checkoutState = useAppSelector((state) => state.CheckoutDocument);
  const flowState = useAppSelector((state) => state.DocumentCheckoutFlow);
  const { onIfsDocumentLoadChange } = useTools();

  const performCheckout = useCallback(
    (
      item,
      tokenRefreshPreAction = DEFAULT_TOKEN_REFRESH_PRE_ACTION,
      rollbackAction = false,
      uploadContext = null,
      flowOptions = null
    ) => {
      if (!item) return undefined;
      const checkoutList = buildCheckoutList(item);
      const isOneDriveDoc = item.isOneDriveDoc === true;
      const operationStatus =
        rollbackAction === true
          ? CHECKOUT_STATUS.undoCheckout
          : item.fileState === CHECKOUT_TYPE.checkout
            ? CHECKOUT_STATUS.undoCheckout
            : CHECKOUT_STATUS.checkout;

      const useLegacy =
        rollbackAction === true || (tokenRefreshPreAction?.retry ?? 0) > 0;

      onIfsDocumentLoadChange(DATA_LOAD_STATUS.refresh);

      if (useLegacy) {
        dispatch(
          onCheckoutDocuments(
            checkoutList,
            operationStatus,
            isOneDriveDoc,
            tokenRefreshPreAction,
            rollbackAction
          )
        );
        return undefined;
      }
      const toastOverrides =
        flowOptions?.suppressSuccessToast === true
          ? { __toast: { showSuccess: false } }
          : {};

      return dispatch(
        performCheckoutThunk({
          checkoutList,
          operationStatus,
          isOneDriveDoc,
          isRollback: rollbackAction,
          uploadContext: uploadContext ?? undefined,
          suppressCheckoutUi: flowOptions?.suppressCheckoutUi === true,
          progressMessage: flowOptions?.progressMessage,
          ...toastOverrides,
        })
      );
    },
    [dispatch, onIfsDocumentLoadChange]
  );

  const performBulkCheckout = useCallback(
    (items, uploadContext = null) => {
      const checkoutList = buildCheckoutListFromItems(items);
      if (checkoutList.length === 0) {
        return undefined;
      }

      onIfsDocumentLoadChange(DATA_LOAD_STATUS.refresh);

      return dispatch(
        performCheckoutThunk({
          checkoutList,
          operationStatus: CHECKOUT_STATUS.checkout,
          isOneDriveDoc: false,
          isRollback: false,
          forceBulkCheckout: true,
          uploadContext: uploadContext ?? undefined,
        })
      );
    },
    [dispatch, onIfsDocumentLoadChange]
  );

  const performBulkUndoCheckout = useCallback(
    (items, uploadContext = null, flowOptions = null) => {
      const checkoutList = buildCheckoutListFromItems(items);
      if (checkoutList.length === 0) {
        return undefined;
      }

      onIfsDocumentLoadChange(DATA_LOAD_STATUS.refresh);

      return dispatch(
        performCheckoutThunk({
          checkoutList,
          operationStatus: CHECKOUT_STATUS.undoCheckout,
          isOneDriveDoc: false,
          isRollback: false,
          forceBulkUndoCheckout: true,
          uploadContext: uploadContext ?? undefined,
          suppressCheckoutUi: flowOptions?.suppressCheckoutUi === true,
          ...(flowOptions?.suppressSuccessToast === true
            ? { __toast: { showSuccess: false } }
            : {}),
        })
      );
    },
    [dispatch, onIfsDocumentLoadChange]
  );

  const undoCheckoutDocument = useCallback(
    (item, tokenRefreshPreAction = DEFAULT_TOKEN_REFRESH_PRE_ACTION) => {
      performCheckout(item, tokenRefreshPreAction, true);
    },
    [performCheckout]
  );

  const getCheckoutButtonText = useCallback((item) => {
    if (!item || !item.fileState) return "Check-out";
    return item.fileState === "Checked In" ? "Check-out" : "Undo check-out";
  }, []);

  return {
    ...checkoutState,
    onCheckoutDocuments: (selectedIfsDocuments, operationStatus, isOneDriveDoc, tokenRefreshPreAction, isRollback) =>
      dispatch(
        onCheckoutDocuments(
          selectedIfsDocuments,
          operationStatus,
          isOneDriveDoc,
          tokenRefreshPreAction,
          isRollback
        )
      ),
    performCheckout,
    performBulkCheckout,
    performBulkUndoCheckout,
    isEligibleForBulkCheckout,
    isEligibleForBulkUndoCheckout,
    undoCheckoutDocument,
    getCheckoutButtonText,
    checkoutFlow: {
      loading: flowState?.loading ?? false,
      message: flowState?.message ?? "",
      isDialogOpen: flowState?.isDialogOpen ?? false,
      dialogVariant: flowState?.dialogVariant ?? "info",
      lastOperationWasCheckout: flowState?.lastOperationWasCheckout ?? false,
      fileToOpen: flowState?.fileToOpen ?? null,
      closeCheckoutDialog: () => dispatch(closeCheckoutDialog()),
      clearFileToOpen: () => dispatch(clearCheckoutFileToOpen()),
    },
  };
}
